import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from '@remix-run/react';
import { type SupportedCurrency, getPrices, formatPrice, CURRENCY_CONFIGS } from '~/utils/currency';
import OfferTimer, { isOfferAvailable, useOfferTimer } from '~/components/ui/OfferTimer';
import { isMobile } from '@/utils/mobile';

interface PricingSectionProps {
  currency: SupportedCurrency;
  onScrollToTop?: () => void;
  onContactSales?: () => void;
  isCollapsed?: boolean;
  buttonCTA?: (billingCycle: 'Monthly' | 'Yearly', planName: 'Pro' | 'Launch', isOfferActive: boolean) => void;
  showLimitedOffer?: boolean;
}

// Background images for each plan card
const CARD_BACKGROUNDS = {
  starter:
    'https://framerusercontent.com/images/cJsiq0q9niRWGtJQLVeGVlbzw.webp?scale-down-to=512&width=2556&height=1712',
  pro: 'https://framerusercontent.com/images/FFqEFtmvUQHyey0bOzSMFtnDjzA.webp?scale-down-to=512&width=2556&height=1712',
  launch:
    'https://framerusercontent.com/images/zcGg9fBUn0mXHf60DOOeX9TDOlg.webp?scale-down-to=512&width=2556&height=1712',
};

const CheckIcon = ({ className = 'text-black/70' }: { className?: string }) => (
  <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${className}`} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const STAR_ICON_URL = 'https://framerusercontent.com/images/w1u97qohOlpwiu14st5FYPwg88U.png?width=120&height=120';

const PlanStarIcon = () => (
  <img
    src={STAR_ICON_URL}
    alt=""
    width={18}
    height={18}
    className="flex-shrink-0"
    style={{ filter: 'brightness(10)' }}
  />
);

// Offer prices per currency (limited-time special price, monthly only)
const OFFER_PRICES: Record<string, number> = {
  USD: 19,
  EUR: 17,
  GBP: 15,
  INR: 1999,
  BRL: 99,
};

export default function PricingSection({
  currency,
  onScrollToTop,
  onContactSales,
  isCollapsed = false,
  buttonCTA,
  showLimitedOffer = true,
}: PricingSectionProps) {
  const location = useLocation();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingButton, setLoadingButton] = useState<string | null>(null);

  // Check if we're on onboarding or pricing pages
  const isOnPricingOrOnboardingPage = location.pathname === '/onboarding' || location.pathname === '/pricing';

  // Get fixed prices for the selected currency
  const prices = getPrices(currency);
  const currencySymbol = CURRENCY_CONFIGS[currency].symbol;

  // Offer timer for Pro plan
  const offerTimer = useOfferTimer();
  const offerPrice = OFFER_PRICES[currency] || OFFER_PRICES.USD;
  const isOfferActive = showLimitedOffer && isOfferAvailable() && offerTimer.isActive && billingCycle === 'monthly';

  const renderFeatures = (features: React.ReactNode[]) => {
    const shouldCollapse = isCollapsed && !isExpanded;
    const displayedFeatures = shouldCollapse ? features.slice(0, 5) : features;

    return displayedFeatures;
  };

  const handleGetStarted = (
    selectedBillingCycle: 'monthly' | 'annual',
    planName: 'Pro' | 'Launch',
    buttonId: string,
  ) => {
    setLoadingButton(buttonId);

    const billingCycleParam = selectedBillingCycle === 'monthly' ? 'Monthly' : 'Yearly';

    if (buttonCTA) {
      buttonCTA(billingCycleParam, planName, isOfferActive);
    } else {
      setLoadingButton(null);

      if (onScrollToTop) {
        onScrollToTop();
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleContactSales = () => {
    if (onContactSales) {
      onContactSales();
    } else {
      window.open('https://calendar.app.google/sAggpvWcDjTWUasj9', '_blank');
    }
  };

  return (
    <div id="pricing" className="w-full bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24 lg:pb-12 scroll-mt-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-6">
          <h1 className="text-black text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ fontFamily: 'Perfectly Nineties' }}>
            One Subscription&nbsp;
            {isMobile() && <br />}
            <span className="font-regular italic" style={{ fontFamily: 'Instrument Serif' }}>
              Infinite&nbsp;
            </span>
            Possibilities.
          </h1>
        </div>

        <div className="flex justify-center mb-6">
          {/* Billing Toggle */}
          <div
            className="inline-flex items-center rounded-full p-1 border border-black/10 bg-black/[0.03]"
            style={{ fontFamily: 'Inter Display' }}
          >
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                billingCycle === 'monthly' ? 'bg-black text-white shadow-sm' : 'text-black/60 hover:text-black/80'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                billingCycle === 'annual' ? 'bg-black text-white shadow-sm' : 'text-black/60 hover:text-black/80'
              }`}
            >
              Annually
              <span className="text-[#4CAF50] text-xs font-semibold">Save 20%</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 max-w-[1400px] mx-auto">
          {/* ============================================= */}
          {/* Starter Plan Card */}
          {/* ============================================= */}
          <div className="flex flex-col overflow-hidden">
            {/* Card Hero Section with Background Image */}
            <div
              className="relative p-5 pb-5"
              style={{
                backgroundImage: `url(${CARD_BACKGROUNDS.starter})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '230px',
              }}
            >
              {/* Dark overlay for readability */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 100%)',
                }}
              />

              <div className="relative z-10 flex flex-col h-full justify-between">
                {/* Plan Name */}
                <div className="flex items-center gap-2 mb-2">
                  <PlanStarIcon />
                  <span
                    className="text-white text-sm font-medium tracking-wide"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    Starter
                  </span>
                </div>

                {/* Price */}
                <div className="mb-2">
                  <div className="flex items-baseline gap-1">
                    <span
                      className="text-white font-normal"
                      style={{
                        fontFamily: 'Perfectly Nineties',
                        fontSize: '40px',
                        lineHeight: '1',
                        letterSpacing: '-1px',
                      }}
                    >
                      {currencySymbol}0
                    </span>
                    <span className="text-white/70 text-xs font-normal" style={{ fontFamily: 'Inter Display' }}>
                      /month
                    </span>
                  </div>
                  <div className="mt-1">
                    <span className="text-white/50 text-xs" style={{ fontFamily: 'Inter Display' }}>
                      Free Forever
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p
                  className="text-white/80 text-xs leading-relaxed mb-2"
                  style={{ fontFamily: 'Inter Display', maxWidth: '280px' }}
                >
                  Everything you need to explore and build your first product.
                </p>

                {/* CTA Button */}
                <button
                  onClick={() => handleGetStarted(billingCycle, 'Launch', 'free')}
                  disabled={!isOnPricingOrOnboardingPage || loadingButton === 'free'}
                  className="w-full bg-white/95 backdrop-blur-sm text-black py-2.5 px-6 rounded-lg font-semibold text-sm transition-all duration-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-white/20"
                  style={{ fontFamily: 'Inter Display' }}
                >
                  {loadingButton === 'free' ? (
                    <div className="i-ph-spinner animate-spin w-5 h-5" />
                  ) : isOnPricingOrOnboardingPage ? (
                    'Start building for free'
                  ) : (
                    'Start building for free'
                  )}
                </button>
              </div>
            </div>

            {/* Features Section */}
            <div className="bg-white px-6 py-6 flex-grow border border-t-0 border-black/8">
              <ul className="space-y-4">
                {renderFeatures([
                  <li key="1" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>25 messages</span>
                  </li>,
                  <li key="2" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="font-medium text-black">Build web & mobile apps</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        iOS and Android apps, SEO optimised pages, responsive dashboards
                      </div>
                    </div>
                  </li>,
                  <li key="3" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div>AI Code Generation</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">Standard AI Models</div>
                    </div>
                  </li>,
                  <li key="4" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Upload existing projects from GitHub</span>
                  </li>,
                  <li key="5" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Community Support</span>
                  </li>,
                  <li key="6" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="font-medium text-black">Variables</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        Build AI apps securely by adding your keys
                      </div>
                    </div>
                  </li>,
                  <li key="7" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div>Prompt Enhancer</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        Prompt optimisation for Anthropic, Google models
                      </div>
                    </div>
                  </li>,
                  <li key="8" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div>Authentication</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        One-click Google & Social Media Login integration
                      </div>
                    </div>
                  </li>,
                  <li key="9" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Code Download</span>
                  </li>,
                  <li key="10" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Templates</span>
                  </li>,
                  <li key="11" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Figma to Code</span>
                  </li>,
                  <li key="12" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Backend Database</span>
                  </li>,
                  <li key="13" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Single-click publish to internet</span>
                  </li>,
                  <li key="14" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Custom Domain</span>
                  </li>,
                ])}
              </ul>
            </div>
          </div>

          {/* ============================================= */}
          {/* Pro Plan Card */}
          {/* ============================================= */}
          <div className="flex flex-col overflow-hidden">
            {/* Card Hero Section with Background Image */}
            <div
              className="relative p-5 pb-5"
              style={{
                backgroundImage: `url(${CARD_BACKGROUNDS.pro})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '230px',
              }}
            >
              {/* Dark overlay for readability */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)',
                }}
              />

              <div className="relative z-10 flex flex-col h-full justify-between">
                {/* Plan Name + Best Value Badge */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <PlanStarIcon />
                    <span
                      className="text-white text-sm font-medium tracking-wide"
                      style={{ fontFamily: 'Inter Display' }}
                    >
                      Pro
                    </span>
                  </div>
                  <span
                    className="bg-white/20 backdrop-blur-md text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/30"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    Best Value
                  </span>
                </div>

                {/* Price */}
                <div className="mb-2">
                  {isOfferActive ? (
                    <>
                      <div className="flex items-baseline gap-2">
                        <span
                          className="text-white font-normal"
                          style={{
                            fontFamily: 'Perfectly Nineties',
                            fontSize: '40px',
                            lineHeight: '1',
                            letterSpacing: '-1px',
                          }}
                        >
                          {formatPrice(offerPrice, currency)}
                        </span>
                        <span
                          className="text-white/50 line-through font-normal"
                          style={{
                            fontFamily: 'Perfectly Nineties',
                            fontSize: '22px',
                            lineHeight: '1',
                          }}
                        >
                          {formatPrice(billingCycle === 'monthly' ? prices.pro.monthly : prices.pro.annual, currency)}
                        </span>
                        <span className="text-white/70 text-xs font-normal" style={{ fontFamily: 'Inter Display' }}>
                          /month
                        </span>
                      </div>
                      {/* Offer timer pill */}
                      <OfferTimer />
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span
                          className="text-white font-normal"
                          style={{
                            fontFamily: 'Perfectly Nineties',
                            fontSize: '40px',
                            lineHeight: '1',
                            letterSpacing: '-1px',
                          }}
                        >
                          {formatPrice(billingCycle === 'monthly' ? prices.pro.monthly : prices.pro.annual, currency)}
                        </span>
                        <span className="text-white/70 text-xs font-normal" style={{ fontFamily: 'Inter Display' }}>
                          /month
                        </span>
                      </div>
                      <div className="mt-1">
                        <span className="text-white/40 text-sm line-through" style={{ fontFamily: 'Inter Display' }}>
                          {formatPrice(prices.pro.original, currency)}/month
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Description */}
                <p
                  className="text-white/80 text-xs leading-relaxed mb-2"
                  style={{ fontFamily: 'Inter Display', maxWidth: '280px' }}
                >
                  More room, higher limits, and priority support to ship faster.
                </p>

                {/* CTA Button */}
                <button
                  onClick={() => handleGetStarted(billingCycle, 'Pro', 'pro')}
                  disabled={loadingButton === 'pro'}
                  className="w-full bg-white/95 backdrop-blur-sm text-black py-2.5 px-6 rounded-lg font-semibold text-sm transition-all duration-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-white/20"
                  style={{ fontFamily: 'Inter Display' }}
                >
                  {loadingButton === 'pro' ? <div className="i-ph-spinner animate-spin w-5 h-5" /> : 'Upgrade to Pro'}
                </button>
              </div>
            </div>

            {/* Features Section */}
            <div className="bg-white px-6 py-6 flex-grow border border-t-0 border-black/8">
              <ul className="space-y-4">
                {renderFeatures([
                  <li key="1" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span className="font-medium text-black" style={{ fontFamily: 'Inter Display' }}>
                      200 Messages
                    </span>
                  </li>,
                  <li key="2" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="font-medium text-black">Build web & mobile apps</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        iOS and Android apps, SEO optimised pages, responsive dashboards
                      </div>
                    </div>
                  </li>,
                  <li key="3" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="font-medium text-black">AI Code Generation</div>
                      <div className="text-xs mt-1 leading-relaxed text-black/50">
                        GPT 5.3 Codex, Claude 4.6, Gemini 3.1
                      </div>
                    </div>
                  </li>,
                  <li key="4" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span className="font-medium text-black" style={{ fontFamily: 'Inter Display' }}>
                      Enhanced limits for GitHub import
                    </span>
                  </li>,
                  <li key="5" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-black">Priority Email & Community Support</span>
                      </div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        We respond within 2 hours on Email and Discord
                      </div>
                    </div>
                  </li>,
                  <li key="6" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="font-medium text-black">Variables</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        Build AI apps securely by adding your keys
                      </div>
                    </div>
                  </li>,
                  <li key="7" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div>Prompt Enhancer</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        Prompt optimisation for Anthropic, Google models
                      </div>
                    </div>
                  </li>,
                  <li key="8" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div>Authentication</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        One-click Google & Social Media Login integration
                      </div>
                    </div>
                  </li>,
                  <li key="9" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Code Download</span>
                  </li>,
                  <li key="10" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Templates</span>
                  </li>,
                  <li key="11" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Figma to Code</span>
                  </li>,
                  <li key="12" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Backend Database</span>
                  </li>,
                  <li key="13" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Single-click publish to internet</span>
                  </li>,
                  <li key="14" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Custom Domain</span>
                  </li>,
                ])}
              </ul>
            </div>
          </div>

          {/* ============================================= */}
          {/* Unlimited Plan Card */}
          {/* ============================================= */}
          <div className="flex flex-col overflow-hidden">
            {/* Card Hero Section with Background Image */}
            <div
              className="relative p-5 pb-5"
              style={{
                backgroundImage: `url(${CARD_BACKGROUNDS.launch})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                height: '230px',
              }}
            >
              {/* Dark overlay for readability */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)',
                }}
              />

              <div className="relative z-10 flex flex-col h-full justify-between">
                {/* Plan Name */}
                <div className="flex items-center gap-2 mb-2">
                  <PlanStarIcon />
                  <span
                    className="text-white text-sm font-medium tracking-wide"
                    style={{ fontFamily: 'Inter Display' }}
                  >
                    Unlimited
                  </span>
                </div>

                {/* Price */}
                <div className="mb-2">
                  <div className="flex items-baseline gap-1">
                    <span
                      className="text-white font-normal"
                      style={{
                        fontFamily: 'Perfectly Nineties',
                        fontSize: '40px',
                        lineHeight: '1',
                        letterSpacing: '-1px',
                      }}
                    >
                      {formatPrice(
                        billingCycle === 'monthly' ? prices.unlimited.monthly : prices.unlimited.annual,
                        currency,
                      )}
                    </span>
                    <span className="text-white/70 text-xs font-normal" style={{ fontFamily: 'Inter Display' }}>
                      /month
                    </span>
                  </div>
                  <div className="mt-1">
                    <span className="text-white/40 text-sm line-through" style={{ fontFamily: 'Inter Display' }}>
                      {formatPrice(prices.unlimited.original, currency)}/month
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p
                  className="text-white/80 text-xs leading-relaxed mb-2"
                  style={{ fontFamily: 'Inter Display', maxWidth: '280px' }}
                >
                  No limits, dedicated support, and full access for serious teams.
                </p>

                {/* CTA Button */}
                <button
                  onClick={() => handleGetStarted(billingCycle, 'Launch', 'launch')}
                  disabled={loadingButton === 'launch'}
                  className="w-full bg-white/95 backdrop-blur-sm text-black py-2.5 px-6 rounded-lg font-semibold text-sm transition-all duration-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-white/20"
                  style={{ fontFamily: 'Inter Display' }}
                >
                  {loadingButton === 'launch' ? (
                    <div className="i-ph-spinner animate-spin w-5 h-5" />
                  ) : (
                    'Upgrade to Unlimited'
                  )}
                </button>
              </div>
            </div>

            {/* Features Section */}
            <div className="bg-white px-6 py-6 flex-grow border border-t-0 border-black/8">
              <ul className="space-y-4">
                {renderFeatures([
                  <li key="1" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span className="font-medium text-black" style={{ fontFamily: 'Inter Display' }}>
                      Unlimited messages
                    </span>
                  </li>,
                  <li key="2" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="font-medium text-black">Build web & mobile apps</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        iOS and Android apps, SEO optimised pages, responsive dashboards
                      </div>
                    </div>
                  </li>,
                  <li key="3" className="flex items-start gap-3 text-sm text-black">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="font-medium text-black">AI Code Generation</div>
                      <div className="text-xs mt-1 leading-relaxed text-black/50">
                        GPT 5.3 Codex, Opus 4.6, Gemini 3.1 Pro
                      </div>
                    </div>
                  </li>,
                  <li key="4" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span className="font-medium text-black" style={{ fontFamily: 'Inter Display' }}>
                      Enhanced limits for GitHub import
                    </span>
                  </li>,
                  <li key="5" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-black">Dedicated Support</span>
                      </div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        Speak to our product expert 1-to-1 anytime
                      </div>
                    </div>
                  </li>,
                  <li key="6" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div className="font-medium text-black">Variables</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        Build AI apps securely by adding your keys
                      </div>
                    </div>
                  </li>,
                  <li key="7" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div>Prompt Enhancer</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        Prompt optimisation for Anthropic, Google models
                      </div>
                    </div>
                  </li>,
                  <li key="8" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <div style={{ fontFamily: 'Inter Display' }}>
                      <div>Authentication</div>
                      <div className="text-black/50 text-xs mt-1 leading-relaxed">
                        One-click Google & Social Media Login integration
                      </div>
                    </div>
                  </li>,
                  <li key="9" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Code Download</span>
                  </li>,
                  <li key="10" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Templates</span>
                  </li>,
                  <li key="11" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Figma to Code</span>
                  </li>,
                  <li key="12" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Backend Database</span>
                  </li>,
                  <li key="13" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Single-click publish to internet</span>
                  </li>,
                  <li key="14" className="flex items-start gap-3 text-sm text-black/80">
                    <CheckIcon />
                    <span style={{ fontFamily: 'Inter Display' }}>Custom Domain</span>
                  </li>,
                ])}
              </ul>
            </div>
          </div>
        </div>

        {/* Common Expand Button */}
        {isCollapsed && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-black text-sm font-medium hover:opacity-70 transition-opacity flex items-center gap-2 px-6 py-3 border border-black/20 rounded-xl hover:border-black/40"
              style={{ fontFamily: 'Inter Display' }}
            >
              {isExpanded ? 'Show Less' : 'Show All Features'}
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Not Sure Card */}
        <div className="max-w-[1400px] mx-auto mt-8">
          <div className="bg-white border border-black/10 rounded-xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 hover:border-black/20 transition-all duration-300 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-black/5 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-black text-xl font-semibold mb-1" style={{ fontFamily: 'Inter Display' }}>
                  Not sure what works for you?
                </h3>
                <p className="text-black/60 text-sm" style={{ fontFamily: 'Inter Display' }}>
                  Speak to our vibe coding specialist and get a free consultation.
                </p>
              </div>
            </div>
            <button
              onClick={handleContactSales}
              className="bg-black text-white py-3 px-8 rounded-xl font-semibold transition-all duration-200 whitespace-nowrap shadow-[0_3px_0_0_rgba(153,161,175,0.5)] hover:shadow-[0_2px_0_0_rgba(153,161,175,1)] hover:translate-y-[1px] active:shadow-[0_1px_0_0_rgba(153,161,175,1)] active:translate-y-[2px]"
              style={{ fontFamily: 'Inter Display' }}
            >
              Talk to us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
